import { useEffect, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, deleteObject, listAll } from "firebase/storage";
import { db, storage } from "../../lib/firebase";
import ImageUploader from "./ImageUploader.jsx";
import DocUploader from "./DocUploader.jsx";
import styles from "./ProjectEditor.module.css";

const STATUS = [
  { value: "in_development", label: "En desarrollo" },
  { value: "delivered", label: "Entregado" },
  { value: "in_service", label: "En servicio" },
];

export default function ProjectEditor() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "projects"), orderBy("updatedAt", "desc"));
    return onSnapshot(q, (snap) =>
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  const selected = items.find((p) => p.id === selectedId) || null;

  async function createNew() {
    const docRef = await addDoc(collection(db, "projects"), {
      name: "Nuevo proyecto",
      shortDescription: "",
      longDescription: "",
      status: "in_development",
      gallery: [],
      docs: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    setSelectedId(docRef.id);
  }

  async function save(field, value) {
    if (!selected) return;
    await updateDoc(doc(db, "projects", selected.id), {
      [field]: value,
      updatedAt: serverTimestamp(),
    });
  }

  async function removeProject() {
    if (!selected) return;
    if (
      !confirm(
        `Eliminar "${selected.name}" y todos sus archivos asociados?`
      )
    )
      return;

    // Borrar Storage (gallery + docs)
    try {
      const folder = ref(storage, `projects/${selected.id}`);
      const all = await listAll(folder);
      const tasks = [];
      const walk = async (dir) => {
        const r = await listAll(dir);
        r.items.forEach((it) => tasks.push(deleteObject(it)));
        for (const p of r.prefixes) await walk(p);
      };
      all.items.forEach((it) => tasks.push(deleteObject(it)));
      for (const p of all.prefixes) await walk(p);
      await Promise.all(tasks.map((t) => t.catch(() => null)));
    } catch {
      /* ignorar */
    }

    await deleteDoc(doc(db, "projects", selected.id));
    setSelectedId(null);
  }

  return (
    <div className={styles.wrap}>
      <aside className={styles.list}>
        <button className={styles.newBtn} onClick={createNew}>
          + Nuevo proyecto
        </button>
        <ul>
          {items.map((p) => (
            <li key={p.id}>
              <button
                className={`${styles.row} ${selectedId === p.id ? styles.active : ""}`}
                onClick={() => setSelectedId(p.id)}
              >
                <span className="mono-tag">
                  {p.id.slice(0, 6).toUpperCase()}
                </span>
                <span className={styles.name}>{p.name || "(sin nombre)"}</span>
              </button>
            </li>
          ))}
          {items.length === 0 && (
            <li className={styles.empty}>// sin proyectos</li>
          )}
        </ul>
      </aside>

      <section className={styles.editor}>
        {!selected ? (
          <p className={styles.placeholder}>
            ↳ selecciona un proyecto a la izquierda o crea uno nuevo.
          </p>
        ) : (
          <>
            <header className={styles.head}>
              <span className="mono-tag">
                // editando · {selected.id}
              </span>
              <button onClick={removeProject} className={styles.del}>
                Eliminar proyecto
              </button>
            </header>

            <div className={styles.fields}>
              <label>
                <span>Nombre</span>
                <input
                  defaultValue={selected.name}
                  onBlur={(e) => save("name", e.target.value)}
                />
              </label>

              <label>
                <span>Estado</span>
                <select
                  defaultValue={selected.status || "in_development"}
                  onChange={(e) => save("status", e.target.value)}
                >
                  {STATUS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>

              <label data-span="full">
                <span>Descripción corta</span>
                <input
                  defaultValue={selected.shortDescription}
                  onBlur={(e) =>
                    save("shortDescription", e.target.value)
                  }
                  placeholder="Una línea que aparece en el listado."
                />
              </label>

              <label data-span="full">
                <span>Descripción larga</span>
                <textarea
                  rows={6}
                  defaultValue={selected.longDescription}
                  onBlur={(e) => save("longDescription", e.target.value)}
                  placeholder="Detalles técnicos, contexto, principios de operación…"
                />
              </label>
            </div>

            <section className={styles.block}>
              <h3>Galería de imágenes</h3>
              <ImageUploader
                collectionPath={`projects/${selected.id}/gallery`}
                storageFolder={`projects/${selected.id}/gallery`}
                existing={[]}
                onChange={() => {}}
              />
              <p className={styles.note}>
                // las imágenes se guardan como subcolección{" "}
                <code>projects/{selected.id}/gallery</code>.
              </p>
            </section>

            <section className={styles.block}>
              <h3>Documentos (fichas, manuales, planos)</h3>
              <DocUploader
                projectId={selected.id}
                docs={selected.docs || []}
                onChange={(docs) => save("docs", docs)}
              />
            </section>
          </>
        )}
      </section>
    </div>
  );
}
